export const activityExtendThrottleMs = 60 * 1000;
export const idleActivityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

export type SessionIdlePolicy = {
  idleTimeoutHours: number;
  warningMinutes: number;
};

export type SessionIdleTimerDeps = {
  /** Slides the server window; false means the session is already gone. */
  extend: () => Promise<boolean>;
  onWarningChange: (visible: boolean) => void;
  onRemainingChange: (remainingSeconds: number) => void;
  onExpired: () => void;
  now: () => number;
  setInterval: (callback: () => void, ms: number) => number;
  clearInterval: (handle: number) => void;
  setTimeout: (callback: () => void, ms: number) => number;
  clearTimeout: (handle: number) => void;
};

export type SessionIdleTimer = {
  start: () => void;
  stop: () => void;
  setPolicy: (policy: SessionIdlePolicy) => void;
  /** The moment the server row expires, in local clock ms — the countdown follows it instead of guessing. */
  setDeadline: (expiresAt: number) => void;
  reportActivity: () => void;
  staySignedIn: () => void;
  hideWarning: () => void;
};

export function createSessionIdleTimer(policy: SessionIdlePolicy, deps: SessionIdleTimerDeps): SessionIdleTimer {
  let idleTimeoutHours = policy.idleTimeoutHours;
  let warningMinutes = policy.warningMinutes;
  let warningHandle: number | null = null;
  let clockHandle: number | null = null;
  let warningVisible = false;
  let timingOut = false;
  let lastExtendAt = 0;
  // Wall-clock deadline: a background tab freezes its timers, so every read compares against this
  let warnAt = 0;

  function getCountdownSeconds(): number {
    return warningMinutes * 60;
  }

  function getWarningDelayMs(): number {
    return Math.max(idleTimeoutHours * 60 * 60 * 1000 - getCountdownSeconds() * 1000, 1000);
  }

  function setWarningVisible(visible: boolean): void {
    warningVisible = visible;
    deps.onWarningChange(visible);
  }

  function clearWarningTimer(): void {
    if (warningHandle !== null) {
      deps.clearTimeout(warningHandle);
      warningHandle = null;
    }
  }

  function restartAt(nextWarnAt: number): void {
    clearWarningTimer();
    timingOut = false;
    if (warningVisible) {
      setWarningVisible(false);
    }
    warnAt = nextWarnAt;
    warningHandle = deps.setTimeout(() => {
      setWarningVisible(true);
    }, Math.max(nextWarnAt - deps.now(), 0));
    // Report the reset now rather than on the next tick, so a clock reading it redraws with the interaction
    tick();
  }

  function restart(): void {
    restartAt(deps.now() + getWarningDelayMs());
  }

  function setDeadline(expiresAt: number): void {
    if (!warningVisible && Number.isFinite(expiresAt)) {
      restartAt(expiresAt - getCountdownSeconds() * 1000);
    }
  }

  async function runTimeout(): Promise<void> {
    // Extending answers both questions at once: it succeeds only while the session lives, and a live session is one
    // another tab or product kept warm, which is exactly when this user should stay signed in
    const alive = await deps.extend();
    if (alive) {
      restart();
    } else {
      deps.onExpired();
    }
  }

  function tick(): void {
    const endsAt = warnAt + getCountdownSeconds() * 1000;
    let remaining = Math.ceil((endsAt - deps.now()) / 1000);
    if (remaining < 0) {
      remaining = 0;
    }
    deps.onRemainingChange(remaining);
    // Not gated on the warning being visible: a user who closes it must still be signed out when the clock runs out
    if (remaining <= 0 && warnAt > 0 && !timingOut) {
      timingOut = true;
      void runTimeout();
    }
  }

  function reportActivity(): void {
    if (!warningVisible) {
      const now = deps.now();
      if (now - lastExtendAt >= activityExtendThrottleMs) {
        lastExtendAt = now;
        void deps.extend();
      }
    }
  }

  function staySignedIn(): void {
    // Dismiss and reschedule BEFORE the awaited extend: a slow request would otherwise race the countdown's last tick
    restart();
    lastExtendAt = deps.now();
    void deps.extend();
  }

  function start(): void {
    restart();
    if (clockHandle === null) {
      clockHandle = deps.setInterval(tick, 1000);
    }
  }

  function stop(): void {
    clearWarningTimer();
    if (clockHandle !== null) {
      deps.clearInterval(clockHandle);
      clockHandle = null;
    }
  }

  function setPolicy(next: SessionIdlePolicy): void {
    if (next.idleTimeoutHours !== idleTimeoutHours || next.warningMinutes !== warningMinutes) {
      idleTimeoutHours = next.idleTimeoutHours;
      warningMinutes = next.warningMinutes;
      restart();
    }
  }

  return {
    start: start,
    stop: stop,
    setPolicy: setPolicy,
    setDeadline: setDeadline,
    reportActivity: reportActivity,
    staySignedIn: staySignedIn,
    hideWarning: () => {
      setWarningVisible(false);
    },
  };
}

/**
 * Binds the DOM sources of user activity — the idle events, tab visibility, and
 * focus — to a timer, returning the unbind. The ONE definition of what counts as
 * activity, shared by the React shells and the server-rendered pages.
 */
export function bindIdleActivity(timer: SessionIdleTimer, windowRef: Window, documentRef: Document): () => void {
  const handleActivity = () => {
    timer.reportActivity();
  };
  const handleVisible = () => {
    if (documentRef.visibilityState === 'visible') {
      timer.reportActivity();
    }
  };
  for (const event of idleActivityEvents) {
    windowRef.addEventListener(event, handleActivity);
  }
  documentRef.addEventListener('visibilitychange', handleVisible);
  windowRef.addEventListener('focus', handleVisible);
  return () => {
    for (const event of idleActivityEvents) {
      windowRef.removeEventListener(event, handleActivity);
    }
    documentRef.removeEventListener('visibilitychange', handleVisible);
    windowRef.removeEventListener('focus', handleVisible);
  };
}
